import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { RootStoreContext } from '../../global/storeContext';
import { firebaseConfig } from '../../configs/firebase.config';
import { MigrationInfo, MigrationRun, MigrationStatusResponse } from '../../utils/apiClients';

export const POLL_INTERVAL_MS = 3000;
/** Polls in a row that may fail before the page stops and asks for a refresh. */
export const MAX_POLL_FAILURES = 5;
const PRODUCTION_PROJECT_ID = 'realhaus-ca';

const isActive = (run?: MigrationRun) => run?.status === 'queued' || run?.status === 'running';
const formatDate = (ms?: number) => (ms ? new Date(ms).toLocaleString('en-CA') : '');
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

type ChipColor = 'default' | 'success' | 'info' | 'warning' | 'error';

const statusChip = (m: MigrationInfo, run?: MigrationRun): { label: string; color: ChipColor } => {
    if (m.status === 'applied') return { label: 'Applied', color: 'success' };
    if (m.status === 'running') return { label: 'Running', color: 'info' };
    const result = run?.results.find((r) => r.id === m.id);
    if (result?.status === 'failed') return { label: 'Failed', color: 'error' };
    if (result?.status === 'skipped') return { label: 'Skipped', color: 'default' };
    if (isActive(run) && run?.pending.includes(m.id)) return { label: 'Queued', color: 'info' };
    return { label: 'Pending', color: 'warning' };
};

const describeRun = (run: MigrationRun, migrations: MigrationInfo[]) => {
    const name = (id?: string) => migrations.find((m) => m.id === id)?.description ?? id;
    if (run.status === 'queued') return `Queued by ${run.requestedBy}, waiting to start…`;
    if (run.status === 'running') {
        const step = Math.min(run.results.length + 1, run.pending.length);
        return `Running ${step} of ${run.pending.length}: ${name(run.current) ?? 'starting…'}`;
    }
    const by = `by ${run.requestedBy} on ${formatDate(run.finishedAt ?? run.requestedAt)}`;
    if (run.status === 'success') return `Last run ${by}: applied ${plural(run.results.length, 'migration')}.`;
    if (run.status === 'interrupted') return `Last run ${by} did not finish. Check the function logs.`;
    const failed = run.results.find((r) => r.status === 'failed');
    return `Last run ${by} failed${failed ? ` on "${name(failed.id)}"` : ''}: ${run.error ?? 'unknown error'}`;
};

export const Migrations: React.FC = observer(() => {
    const { dashboardStore, uiStore } = useContext(RootStoreContext);
    const [status, setStatus] = useState<MigrationStatusResponse>();
    const [loadFailed, setLoadFailed] = useState(false);
    const [polling, setPolling] = useState(false);
    const [starting, setStarting] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    // A ref, not state, so a second click before React re-renders is still caught.
    const startingRef = useRef(false);
    const pollFailures = useRef(0);

    const load = useCallback(async () => {
        try {
            const next = await dashboardStore.getMigrationStatus();
            if (next) {
                setStatus(next);
                setLoadFailed(false);
                pollFailures.current = 0;
            }
            return next;
        } catch (err) {
            console.error(err);
            pollFailures.current += 1;
            setLoadFailed(true);
            return undefined;
        }
    }, [dashboardStore]);

    // Load on open, and pick up a run that is already active (another admin's, or before a reload).
    useEffect(() => {
        void load().then((s) => {
            if (isActive(s?.run)) setPolling(true);
        });
    }, [load]);

    useEffect(() => {
        if (!polling) return;
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout>;

        const announce = (s: MigrationStatusResponse) => {
            const run = s.run;
            if (run?.status === 'success') {
                uiStore.success(`Applied ${plural(run.results.length, 'migration')}`);
            } else if (run) {
                uiStore.error(describeRun(run, s.migrations));
            }
        };

        const tick = async () => {
            const s = await load();
            if (cancelled) return;
            if (!s) {
                if (pollFailures.current >= MAX_POLL_FAILURES) {
                    setPolling(false);
                    uiStore.error('Lost track of the migration run. Refresh to see its status.');
                    return;
                }
            } else if (!isActive(s.run)) {
                setPolling(false);
                announce(s);
                return;
            }
            timer = setTimeout(tick, POLL_INTERVAL_MS);
        };

        timer = setTimeout(tick, POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [polling, load, uiStore]);

    const startRun = async () => {
        if (startingRef.current) return;
        startingRef.current = true;
        setStarting(true);
        setConfirmOpen(false);
        try {
            const result = await dashboardStore.startMigrationRun();
            if (result?.status === 'noop') {
                uiStore.success('Nothing to migrate: every migration is applied.');
                await load();
            } else if (result) {
                if (result.status === 'running') {
                    uiStore.error(`A migration run started by ${result.running.by} is already in progress.`);
                }
                await load();
                setPolling(true);
            }
        } catch (err) {
            console.error(err);
            uiStore.error('Could not start the migration run.');
        } finally {
            startingRef.current = false;
            setStarting(false);
        }
    };

    const run = status?.run;
    const active = isActive(run) || polling;
    const projectMismatch = !!status && status.projectId !== firebaseConfig.projectId;
    const isProduction = status?.projectId === PRODUCTION_PROJECT_ID;
    const canRun = !!status && status.pendingCount > 0 && !active && !starting && !projectMismatch;
    const progress = run && run.pending.length ? (run.results.length / run.pending.length) * 100 : 0;

    const runLabel = () => {
        if (starting) return 'Starting…';
        if (active) return `Migration in progress${run ? ` (started by ${run.requestedBy})` : ''}`;
        if (!status?.pendingCount) return 'No pending migrations';
        return `Run ${plural(status.pendingCount, 'pending migration')}`;
    };

    return (
        <Box>
            <Typography variant='h5' sx={{ mb: 2 }}>
                Migrations
            </Typography>

            {status && (
                <Alert severity={isProduction ? 'warning' : 'info'} sx={{ mb: 2 }}>
                    Environment: <strong>{status.projectId || 'unknown'}</strong>
                    {isProduction ? ' (production)' : ''}
                </Alert>
            )}
            {projectMismatch && (
                <Alert severity='error' sx={{ mb: 2 }}>
                    The server reports project "{status?.projectId || 'unknown'}", but this portal is configured for "
                    {firebaseConfig.projectId}". Running is disabled.
                </Alert>
            )}
            {loadFailed && !polling && (
                <Alert
                    severity='error'
                    sx={{ mb: 2 }}
                    action={
                        <Button color='inherit' size='small' onClick={() => void load()}>
                            Refresh
                        </Button>
                    }
                >
                    Could not load the migration status.
                </Alert>
            )}

            {status && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                        <Box>
                            <Typography variant='h6' data-testid='migration-summary'>
                                {status.pendingCount ? `${plural(status.pendingCount, 'migration')} pending` : 'Up to date'}
                            </Typography>
                            {run && (
                                <Typography variant='body2' color='text.secondary' data-testid='migration-run'>
                                    {describeRun(run, status.migrations)}
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button variant='outlined' onClick={() => void load()} disabled={starting}>
                                Refresh
                            </Button>
                            <Button variant='contained' disabled={!canRun} onClick={() => setConfirmOpen(true)}>
                                {runLabel()}
                            </Button>
                        </Box>
                    </Box>
                    {isActive(run) && (
                        <LinearProgress
                            sx={{ mt: 2 }}
                            variant={run?.status === 'queued' ? 'indeterminate' : 'determinate'}
                            value={progress}
                        />
                    )}
                </Paper>
            )}

            {status && (
                <TableContainer component={Paper}>
                    <Table size='small'>
                        <TableHead>
                            <TableRow>
                                <TableCell>Migration</TableCell>
                                <TableCell>Timestamp</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Applied</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {status.migrations.map((m) => {
                                const chip = statusChip(m, run);
                                return (
                                    <TableRow key={m.id} data-testid={`migration-${m.id}`}>
                                        <TableCell>
                                            <Typography variant='body2'>{m.description}</Typography>
                                            <Typography variant='caption' color='text.secondary'>
                                                {m.id}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant='body2'>{formatDate(m.timestamp)}</Typography>
                                            <Typography variant='caption' color='text.secondary'>
                                                {m.timestamp}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                <Chip size='small' label={chip.label} color={chip.color} />
                                                {m.outOfOrder && (
                                                    <Tooltip title='Its timestamp is older than the last migration applied. Check it does not depend on, or undo, a later one.'>
                                                        <Chip size='small' variant='outlined' color='warning' label='Out of order' />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {m.legacy && (
                                                <Typography variant='body2' color='text.secondary'>
                                                    Before tracking
                                                </Typography>
                                            )}
                                            {m.appliedAt && (
                                                <Typography variant='body2'>
                                                    {formatDate(m.appliedAt)}
                                                    <br />
                                                    <Typography variant='caption' color='text.secondary'>
                                                        {m.appliedBy}
                                                    </Typography>
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Run pending migrations?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This applies {plural(status?.pendingCount ?? 0, 'migration')} to{' '}
                        <strong>{status?.projectId}</strong>
                        {isProduction ? ' (production)' : ''}, in timestamp order, stopping at the first failure. It
                        cannot be undone from here.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button
                        variant='contained'
                        color={isProduction ? 'warning' : 'primary'}
                        disabled={starting}
                        onClick={() => void startRun()}
                    >
                        Run migrations
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
});
